<template>
  <div class="container">
    <h2>{{ t('overview.title') }}</h2>

    <div class="filters">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="t('common.search')"
        class="search-input"
      />

      <div class="type-filters">
        <button
          :class="['filter-btn', { active: selectedType === null }]"
          @click="selectedType = null"
        >
          {{ t('overview.allRoles') }}
        </button>
        <button
          v-for="type in roleTypes"
          :key="type"
          :class="['filter-btn', `filter-${type}`, { active: selectedType === type }]"
          @click="selectedType = type"
        >
          {{ t(`roleTypes.${type}`) }}
        </button>
      </div>
    </div>

    <div class="roles-grid">
      <div
        v-for="role in filteredRoles"
        :key="role.id"
        class="role-card card"
        @click="selectRole(role)"
      >
        <div class="role-header">
          <h3>{{ t(role.nameKey) }}</h3>
          <span :class="['badge', `badge-${role.type}`]">
            {{ t(`roleTypes.${role.type}`) }}
          </span>
        </div>
        <p class="role-description">{{ t(role.descriptionKey) }}</p>
      </div>
    </div>

    <RoleDetailModal
      v-if="selectedRoleData"
      :role="selectedRoleData"
      @close="selectedRoleData = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Role, RoleType } from '@botc/shared';
import { useRoles } from '../composables/useRoles';
import RoleDetailModal from '../components/RoleDetailModal.vue';

const { t } = useI18n();
const { roles } = useRoles();

const searchQuery = ref('');
const selectedType = ref<RoleType | null>(null);
const selectedRoleData = ref<Role | null>(null);

const roleTypes = [RoleType.TOWNSFOLK, RoleType.OUTSIDER, RoleType.MINION, RoleType.DEMON];

const filteredRoles = computed(() => {
  return roles.value.filter(role => {
    const matchesSearch = searchQuery.value === '' ||
      t(role.nameKey).toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      t(role.descriptionKey).toLowerCase().includes(searchQuery.value.toLowerCase());

    const matchesType = selectedType.value === null || role.type === selectedType.value;

    return matchesSearch && matchesType;
  });
});

function selectRole(role: Role): void {
  selectedRoleData.value = role;
}
</script>

<style scoped>
.filters {
  margin-bottom: var(--spacing-xl);
}

.search-input {
  margin-bottom: var(--spacing-md);
  font-size: 1rem;
}

.type-filters {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.filter-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--color-bg-tertiary);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 0.875rem;
  font-weight: 500;
}

.filter-btn:hover {
  border-color: var(--color-text-muted);
}

.filter-btn.active {
  border-color: var(--color-accent-gold);
  color: var(--color-accent-gold);
}

.filter-townsfolk.active {
  border-color: var(--color-townsfolk);
  color: var(--color-townsfolk);
}

.filter-outsider.active {
  border-color: var(--color-outsider);
  color: var(--color-outsider);
}

.filter-minion.active {
  border-color: var(--color-minion);
  color: var(--color-minion);
}

.filter-demon.active {
  border-color: var(--color-demon);
  color: var(--color-demon);
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-md);
}

.role-card {
  cursor: pointer;
  transition: all var(--transition-normal);
}

.role-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.role-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
}

.role-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.role-description {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 768px) {
  .roles-grid {
    grid-template-columns: 1fr;
  }
}
</style>
